import { inject, Injectable } from '@angular/core';
import { TransitionService } from '@uirouter/core';
import { Observable, Subject } from 'rxjs';
import { DataAnalyticsEvent, DataAnalyticsInitialData } from '@tdwb/core';
import type { Transition, StateDeclaration } from '@uirouter/core';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsRouterConfigService {

  dataAnalyticsEvent: Subject<DataAnalyticsEvent> = new Subject<DataAnalyticsEvent>();
  private initData: DataAnalyticsInitialData;
  private readonly transitionService = inject(TransitionService);

  initHooks(initData: DataAnalyticsInitialData): Observable<DataAnalyticsEvent> {
    this.initData = initData;
    this.transitionService.onSuccess({ to: 'page.**' }, (transition: Transition): void => {
      const transitionTo = transition.to().name;
      const isToOrderStatusPage = transitionTo.includes('page.order-status');
      const eventData = this.getRouteStateEvent(transitionTo);
      const isFromModal = transition.from().name?.startsWith('modal.');

      if(isToOrderStatusPage || (eventData && !isFromModal)) {
        this.dataAnalyticsEvent.next(eventData);
      }
    });

    this.transitionService.onEnter({ entering: 'page.research' }, (transition: Transition, state: StateDeclaration): void => {
      if (transition.params() && Object.keys(transition.params()).length > 1 && !Object.keys(transition.params()).includes('docKey')){
        const partnerFields = transition.params().PARTNER_FIELDS.split('/');
        const researchState = `${state.name}.${partnerFields[0]}.${partnerFields[1]}`;
        const eventData = this.getRouteStateEvent(researchState.toLowerCase());
        if (eventData){
          this.dataAnalyticsEvent.next(eventData);
        }
      }
    });

    this.transitionService.onEnter({ entering: 'page.research.dashboard' }, (): void => {
      const researchState = 'page.research.dashboard';
      const eventData = this.getRouteStateEvent(researchState);
      if (eventData){
        this.dataAnalyticsEvent.next(eventData);
      }
    });

    this.transitionService.onEnter({ entering: 'modal.watchlist.single' }, (transition: Transition, state: StateDeclaration): void => {
      if (transition.params()){
        const tabType = transition.params().tabType?.toLowerCase().includes('watchlists') ? 'watchlists' : 'quotes'
        const researchState = `${state.name}.${tabType}`;
        const eventData = this.getRouteStateEvent(researchState.toLowerCase());
        if (eventData){
          this.dataAnalyticsEvent.next(eventData);
        }
      }
    });

    this.transitionService.onSuccess({ to: 'modal.**' }, (transition: Transition) => {
      const eventData = this.getRouteStateEvent(transition.to().name);
      if(eventData) {
        this.dataAnalyticsEvent.next(eventData);
      }
    });

    // This is to fire event when the 'edit' page is accessed directly from the menu bar.
    // Also, it prevents 'edit' page event getting fired x2 transitioning from review -> edit modal
    this.transitionService.onEnter({ entering: 'modal.trading.order.**' }, (transition: Transition, state: StateDeclaration): void => {
      if (['equity', 'option', 'strategy', 'mutualfund', 'gic', 'hisa'].includes(state.name.substring(state.name.lastIndexOf('.') + 1))){
        const actualStateName = `${transition.to().name}`;
        const eventData: DataAnalyticsEvent = this.getRouteStateEvent(actualStateName + '-edit');
        if(eventData) {
          //re-define event object schema?
          if (transition.from().name === 'page.order-status'){
            eventData.page['customPageName'] = 'wb_modal-trading-order-status-copy';
            eventData.page['subSection2'] = 'order-status';
          }
          this.dataAnalyticsEvent.next(eventData);
        }
      }
    });

    this.transitionService.onRetain({ retained: 'modal.trading.order.**' }, (transition: Transition, state: StateDeclaration): void => {
      if (['equity', 'option', 'strategy', 'mutualfund', 'gic', 'hisa'].includes(state.name.substring(state.name.lastIndexOf('.') + 1))){
        const retainedStateName = `${state.name}-${transition.params().actionType}`;
        const eventData = this.getRouteStateEvent(retainedStateName);
        if (eventData){
          if (transition.params().actionType === 'complete'){
            const equityType = transition.params().equityType;
            const priceType = transition.params().priceType;

            eventData['application'] = { equityType, priceType };
          }
          this.dataAnalyticsEvent.next(eventData);
        }
      }
    });

    const closeModalData = this.getModalCloseEvent();
    if(closeModalData) {
      this.transitionService.onSuccess({ exiting: 'modal.**' }, () => {
        this.dataAnalyticsEvent.next(closeModalData);
      });
    }

    return this.dataAnalyticsEvent.asObservable();
  }

  private getRouteStateEvent(name: string): DataAnalyticsEvent | undefined {
    return this.initData?.routeStateEventMapper?.[name];
  }

  private getModalCloseEvent(): DataAnalyticsEvent {
    return this.initData?.modalCloseEvent;
  }
}
